/**
 * Integrytas Group - Document Generation (DOC)
 * Service: DocService  (BASE SERVICE)
 *
 * Renders a doc_template for a record: fetches the primary row + related
 * bindings UNDER THE CALLER'S JWT (so RLS is respected), merges {{tokens}} and
 * {{#section name}}…{{/section}} repeating blocks in TypeScript, stores the
 * result in DMS (versioned), and records a doc_generated row. PDF output for
 * mail-merge is retired (Gotenberg removed, decision 2); banded reports render
 * via the pure-JS doc-worker.
 *
 *   await generateDocument('DOC.PO_CONTRACT', '1024', { session });
 */

import { apiDb } from '../../lib/api_client';
import { uploadNewDocument } from '../dms/DmsService';
import type { AuthSession } from '../../types/auth';

export interface DocTemplate {
  doc_template_id: number; internal_code: string; name: string;
  primary_entity: string; pk_column: string; output_default: 'HTML' | 'PDF';
}
interface DocBinding { binding_name: string; related_entity: string; fk_column: string; order_by: string | null }
type Row = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Merge engine
// ---------------------------------------------------------------------------
function escapeHtml(v: unknown): string {
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function substitute(tpl: string, obj: Row, prefix: string | null): string {
  return tpl.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (whole, token: string) => {
    let key = token;
    if (prefix) {
      if (token.startsWith(prefix + '.')) key = token.slice(prefix.length + 1);
      else return whole;            // not a row token — leave for the primary pass
    } else if (token.includes('.')) {
      return whole;                 // dotted tokens belong to a section scope
    }
    const v = obj?.[key];
    return v === null || v === undefined ? '' : escapeHtml(v);
  });
}

/** Expand repeating sections first (over their binding rows), then primary tokens. */
export function renderTemplate(body: string, primary: Row, bindings: Record<string, Row[]>): string {
  const withSections = body.replace(
    /\{\{#section\s+([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/section\}\}/g,
    (_m, name: string, inner: string) => (bindings[name] ?? []).map(r => substitute(inner, r, 'row')).join(''),
  );
  return substitute(withSections, primary, null);
}

// ---------------------------------------------------------------------------
// PDF — the external Gotenberg (doc-render) renderer is retired (decision 2).
// MAILMERGE PDF materialization now belongs to the pure-JS doc-worker; until a
// MAILMERGE flow path is wired there, PDF output is served by a banded report
// (Report Designer). HTML merge is unaffected, and no production MAILMERGE
// templates exist (decision 6), so this path is currently unreached.
// ---------------------------------------------------------------------------
function htmlToPdfBytes(_html: string, _filename: string): Promise<Uint8Array> {
  return Promise.reject(new Error(
    'DOC.PDF.MAILMERGE_RETIRED: PDF for mail-merge templates has moved off the external renderer. ' +
    'Use a banded report for PDF output, or export HTML.',
  ));
}

// ---------------------------------------------------------------------------
// Generate
// ---------------------------------------------------------------------------
export interface GenerateResult { html: string; format: 'HTML' | 'PDF'; dmsDocumentId: number | null; generatedId: number | null; title: string }

export async function generateDocument(
  templateCode: string, recordId: string,
  opts: { session: AuthSession; format?: 'HTML' | 'PDF'; locale?: string },
): Promise<GenerateResult> {
  const userId = opts.session.effective_user.user_id;

  // Template + localized body + bindings.
  const { data: tpl, error: e1 } = await apiDb.from('doc_template')
    .select('doc_template_id, internal_code, name, primary_entity, pk_column, output_default')
    .eq('internal_code', templateCode).single<DocTemplate>();
  if (e1 || !tpl) throw new Error(e1?.message ?? 'DOC.TEMPLATE_NOT_FOUND');

  const locale = opts.locale ?? opts.session.effective_user.preferred_locale ?? 'EN_US';
  const { data: tlRows } = await apiDb.from('doc_template_tl')
    .select('locale_code, title, body_html').eq('doc_template_id', tpl.doc_template_id);
  const tls = (tlRows ?? []) as { locale_code: string; title: string | null; body_html: string | null }[];
  const tl = tls.find(t => t.locale_code === locale) ?? tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
  if (!tl?.body_html) throw new Error('DOC.NO_BODY');

  const { data: bRows } = await apiDb.from('doc_binding')
    .select('binding_name, related_entity, fk_column, order_by').eq('doc_template_id', tpl.doc_template_id).eq('is_active', 'Y');
  const bindings = (bRows ?? []) as DocBinding[];

  // Data — fetched under the caller's JWT (RLS applies).
  const { data: primary, error: e2 } = await apiDb.from(tpl.primary_entity).select('*').eq(tpl.pk_column, recordId).maybeSingle();
  if (e2) throw new Error(e2.message);
  if (!primary) throw new Error('DOC.RECORD_NOT_FOUND_OR_FORBIDDEN');

  const bindingData: Record<string, Row[]> = {};
  for (const b of bindings) {
    let q = apiDb.from(b.related_entity).select('*').eq(b.fk_column, recordId);
    if (b.order_by) q = q.order(b.order_by, { ascending: true });
    const { data: rows } = await q;
    bindingData[b.binding_name] = (rows ?? []) as Row[];
  }

  const html = renderTemplate(tl.body_html, primary as Row, bindingData);
  const title = substitute(tl.title ?? tpl.name, primary as Row, null);
  const format: 'HTML' | 'PDF' = opts.format ?? tpl.output_default;

  // Build the output file.
  const safe = title.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120) || 'document';
  let file: File;
  if (format === 'PDF') {
    const bytes = await htmlToPdfBytes(html, `${safe}.pdf`);
    file = new File([bytes as BlobPart], `${safe}.pdf`, { type: 'application/pdf' });
  } else {
    file = new File([html], `${safe}.html`, { type: 'text/html' });
  }

  // Store in DMS (versioned) under the source record.
  let dmsDocumentId: number | null = null;
  try {
    const doc = await uploadNewDocument({ targetEntityName: tpl.primary_entity, targetRecordId: recordId, actorUserId: userId }, file);
    dmsDocumentId = doc.document_id;
  } catch { /* storage optional — still return the rendered output */ }

  // Record the generated instance.
  let generatedId: number | null = null;
  try {
    const { data: gen } = await apiDb.from('doc_generated').insert({
      doc_template_id: tpl.doc_template_id, entity_name: tpl.primary_entity, record_id: recordId,
      locale_code: locale, output_format: format, status: 'RENDERED', title,
      dms_document_id: dmsDocumentId, generated_by_user_id: userId, generated_at: new Date().toISOString(),
    }).select('doc_generated_id').single();
    generatedId = (gen as { doc_generated_id: number } | null)?.doc_generated_id ?? null;
  } catch { /* registry optional */ }

  return { html, format, dmsDocumentId, generatedId, title };
}

export const DocService = { renderTemplate, generateDocument };
